import { useQueryClient } from '@tanstack/react-query';
import { api, hooksApi } from '@verifime/api-definition';
import type { TEntityChoice } from '@verifime/components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toEntityConfirmation } from '../entityConfirmation';
import { HELD_DOCUMENT_TYPE_NOT_ACCEPTED_CODE } from './heldDocumentTypeRefusal';
import { errorCodeOf, statusOf } from './httpStatus';

/** The confirmation endpoint answers 409 when the choice cannot be confirmed for this invite. */
const HTTP_CONFLICT = 409;

/**
 * The `errorCode` person-api supplies on the 409 for a confirmation that would duplicate an
 * identity the client already holds (`DuplicateIdentityRequestException`, R15), once person-api
 * PR 2104 is deployed. Dev answers the 409 with no code until then, so the refusal is not mapped
 * there yet. Matched by this code, never by the message.
 */
const DUPLICATE_IDENTITY_REQUEST_CODE = 'DUPLICATE_IDENTITY_REQUEST';

/**
 * The outcome of a confirmation, discriminated by a string (`outcome`) so it narrows in apps that
 * compile this source without strictNullChecks.
 *
 * - `confirmed`: 204, and the requests have been refetched.
 * - `conflict`: 409 (an open Legacy request for the client, the invite was not decided AskFirst, the
 *   entity is already granted, the invite is already complete, or a concurrent change). No error is
 *   shown: the caller routes, by the `errorCode` person-api supplies on the body (read raw and
 *   passed on as it is; undefined when there is none). Which codes mean what is the caller's.
 * - `duplicate-identity`: 409 with the duplicate identity `errorCode` (R15): the client already
 *   holds this identity, so no request was opened. The requests have been refetched. No error is
 *   shown here and the caller does not route: it tells the customer.
 * - `held-document-type-not-accepted`: 409 with the held document type `errorCode`: the customer
 *   holds a photo card or ImmiCard that the client has not opted into, so no request was opened.
 *   The requests have been refetched. No error is shown here and the caller does not route: it
 *   tells the customer.
 * - `failed`: anything else, with the HTTP status when there was a response. The error shows.
 * - `in-flight`: a confirmation was already running, so this one was not sent.
 */
export type TConfirmEntityChoiceResult =
  | { outcome: 'confirmed' }
  | { outcome: 'conflict'; errorCode?: string }
  | { outcome: 'duplicate-identity' }
  | { outcome: 'held-document-type-not-accepted' }
  | { outcome: 'failed'; status: number | undefined }
  | { outcome: 'in-flight' };

/**
 * Confirms the customer's choice for the client behind a tracking reference
 * (`PUT /v1/tracking/{trackingReference}/confirmation`), which creates the Individual request and
 * the entity request (if any). The endpoint sets the choice, so it can be called again to change
 * it: a different entity replaces the open entity request, the same choice is a 204.
 *
 * On 204 or 409 it refetches `GET /v1/permission/request/me` and resolves only after that, so the
 * caller routes on the server's requests. A failure other than 409 sets `hasError` until the next
 * confirmation.
 */
export function useConfirmEntityChoice(trackingReference: string) {
  const queryClient = useQueryClient();
  const [hasError, setHasError] = useState(false);
  // Synchronous guards: state updates land too late to stop a double click.
  const isConfirmingRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const setErrorIfMounted = useCallback((error: boolean) => {
    if (isMountedRef.current) {
      setHasError(error);
    }
  }, []);

  // Awaited, and inactive queries refetched too (`refetchType: 'all'`), so the caller routes on the
  // server's requests. A failed refetch does not throw (react-query's default); readers retry.
  const refreshRequests = useCallback(
    () =>
      queryClient.invalidateQueries(hooksApi.getKeyByAlias('getV1permissionrequestme'), {
        refetchType: 'all',
      }),
    [queryClient],
  );

  const confirm = useCallback(
    async (choice: TEntityChoice): Promise<TConfirmEntityChoiceResult> => {
      if (isConfirmingRef.current) {
        return { outcome: 'in-flight' };
      }
      if (!trackingReference) {
        console.error('Confirming the entity choice failed: no tracking reference');
        setErrorIfMounted(true);
        return { outcome: 'failed', status: undefined };
      }

      isConfirmingRef.current = true;
      setErrorIfMounted(false);
      try {
        await api.putV1trackingTrackingReferenceconfirmation(toEntityConfirmation(choice), {
          params: { trackingReference },
        });
        await refreshRequests();
        return { outcome: 'confirmed' };
      } catch (error) {
        const status = statusOf(error);
        if (status === HTTP_CONFLICT) {
          await refreshRequests();
          const errorCode = errorCodeOf(error);
          if (errorCode === DUPLICATE_IDENTITY_REQUEST_CODE) {
            return { outcome: 'duplicate-identity' };
          }
          if (errorCode === HELD_DOCUMENT_TYPE_NOT_ACCEPTED_CODE) {
            return { outcome: 'held-document-type-not-accepted' };
          }
          return { outcome: 'conflict', errorCode };
        }
        console.error('Confirming the entity choice failed', { status });
        setErrorIfMounted(true);
        return { outcome: 'failed', status };
      } finally {
        isConfirmingRef.current = false;
      }
    },
    [trackingReference, refreshRequests, setErrorIfMounted],
  );

  return { confirm, hasError };
}
